import { MCQQuestion, QuestionTypesEnum } from 'src/@types/Course';

// The unified website-wide quiz template (identical to the instructor assessment
// template): one row per question, up to 4 bilingual options, `correct` = 1-based
// option number(s) (";"-separated for multi), optional `answer`/`answer_ar` to
// mark the correct option by text.
export const QUESTIONS_TEMPLATE_HEADER =
  'question,question_ar,option1_en,option2_en,option3_en,option4_en,option1_ar,option2_ar,option3_ar,option4_ar,points,answer,answer_ar,correct';

export function buildQuestionsTemplateCsv(): string {
  const rows: string[][] = [
    ['What is 2 + 2?', 'كم يساوي ٢ + ٢؟', '3', '4', '5', '6', '٣', '٤', '٥', '٦', '1', '4', '٤', '2'],
    ['Pick the primary colors', 'اختر الألوان الأساسية', 'Red', 'Green', 'Blue', 'Yellow', 'أحمر', 'أخضر', 'أزرق', 'أصفر', '2', '', '', '1;3'],
  ];
  const cell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const body = rows.map((r) => r.map(cell).join(',')).join('\r\n');
  return [QUESTIONS_TEMPLATE_HEADER, body].join('\r\n') + '\r\n';
}

export function downloadQuestionsTemplate(filename = 'quiz-template.csv'): void {
  // BOM so Excel opens the Arabic columns correctly.
  const blob = new Blob(['﻿', buildQuestionsTemplateCsv()], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// #2 quiz import: parse a questions file into the MCQQuestion template.
//
// JSON: an array (or { questions: [...] }) of objects like
//   { "question": "...", "question_ar": "...", "description": "...",
//     "answers": [{ "name": "...", "name_ar": "...", "points": 1,
//                   "minusPoints": 0, "correct": true }, ...] }
//   (options can also be plain strings + a `correct` index / index-array.)
//
// CSV — two supported layouts:
//   • FULL (one row per answer, grouped by the question text) with columns:
//     question, question_ar, description, description_ar, answer, answer_ar,
//     points, minusPoints, correct (true/false). ← richest; see exam.csv.
//   • SIMPLE (one row per question): question, option1..optionN, correct
//     (1-based option number; use `;` for several correct answers).

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQ = !inQ;
      }
    } else if (c === ',' && !inQ) {
      out.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

const truthy = (s: string) => /^(true|1|yes|y|correct|✓)$/i.test((s || '').trim());
const numOr = (s: string, fallback: number) => {
  const t = (s || '').trim();
  if (t === '' || Number.isNaN(Number(t))) return fallback;
  return Number(t);
};

type FullAnswer = {
  name: string;
  name_ar?: string;
  points: number;
  minusPoints: number;
  correct: boolean;
};

function buildMCQ(parts: {
  title: string;
  title_ar?: string;
  description?: string;
  description_ar?: string;
  answers: FullAnswer[];
}): MCQQuestion {
  return {
    title: parts.title,
    title_ar: parts.title_ar || undefined,
    description: parts.description || undefined,
    description_ar: parts.description_ar || undefined,
    multi: parts.answers.filter((a) => a.correct).length > 1,
    type: QuestionTypesEnum.MCQ,
    // name_ar + minusPoints aren't in the FE QuestionAnswer type but are stored
    // server-side — cast so they're carried through on save.
    answers: parts.answers as any,
  } as MCQQuestion;
}

export async function parseQuestionsFile(file: File): Promise<MCQQuestion[]> {
  const text = await file.text();
  const name = (file.name || '').toLowerCase();

  if (name.endsWith('.json')) {
    const parsed = JSON.parse(text);
    const arr: any[] = Array.isArray(parsed) ? parsed : parsed?.questions || [];
    return arr
      .map((q) => {
        const rawOpts = q.options || q.answers || q.choices || [];
        const answers: FullAnswer[] = rawOpts
          .map((o: any, i: number) => {
            if (typeof o === 'string') {
              const correct = Array.isArray(q.correct)
                ? q.correct.includes(i)
                : q.correct === i || q.correctIndex === i;
              return { name: o, points: correct ? 1 : 0, minusPoints: 0, correct: !!correct };
            }
            const correct = !!(
              o.correct ?? (Array.isArray(q.correct) ? q.correct.includes(i) : false)
            );
            return {
              name: o.name || o.text || '',
              name_ar: o.name_ar || o.text_ar,
              points: Number(o.points ?? (correct ? 1 : 0)) || 0,
              minusPoints: Number(o.minusPoints ?? 0) || 0,
              correct,
            };
          })
          .filter((a: FullAnswer) => a.name !== '');
        return buildMCQ({
          title: q.question || q.title || '',
          title_ar: q.question_ar || q.title_ar,
          description: q.description,
          description_ar: q.description_ar,
          answers,
        });
      })
      .filter((q) => q.title && q.answers.length);
  }

  // CSV / TXT
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const header = splitCsvLine(lines.shift() as string).map((h) => h.toLowerCase().trim());
  const col = (names: string[]) => header.findIndex((h) => names.includes(h));
  const qi = col(['question', 'title']);

  // ── UNIFIED bilingual layout (website-wide template): one row per question,
  // option1_en..N + option1_ar..N, `points`, `correct` (1-based option number(s),
  // ";"-separated for multi) and optional `answer`/`answer_ar` (mark correct by
  // text). Detected by the presence of option{n}_en columns. ──────────────────
  const bilOpts: { en: number; ar: number }[] = [];
  for (let n = 1; n <= 12; n += 1) {
    const en = col([`option${n}_en`]);
    const ar = col([`option${n}_ar`]);
    if (en >= 0) bilOpts.push({ en, ar });
  }
  if (qi >= 0 && bilOpts.length) {
    const qiAr = col(['question_ar', 'title_ar']);
    const pts = col(['points', 'point']);
    const corr = col(['correct', 'iscorrect', 'is_correct', 'correctindex']);
    const answerCol = col(['answer', 'correct_answer']);
    const answerArCol = col(['answer_ar', 'correct_answer_ar']);
    return lines
      .map((line): MCQQuestion | null => {
        const c = splitCsvLine(line);
        const title = (c[qi] || '').trim();
        if (!title) return null;
        const rawOpts = bilOpts
          .map(({ en, ar }) => ({
            name: (c[en] || '').trim(),
            name_ar: ar >= 0 ? (c[ar] || '').trim() : '',
          }))
          .filter((o) => o.name !== '');
        const correctTokens = (corr >= 0 ? c[corr] || '' : '')
          .split(/[;|]/)
          .map((s) => s.trim())
          .filter(Boolean);
        const answerText = answerCol >= 0 ? (c[answerCol] || '').trim() : '';
        const answerArText = answerArCol >= 0 ? (c[answerArCol] || '').trim() : '';
        const questionPoints = pts >= 0 ? numOr(c[pts], 1) : 1;
        let answers: FullAnswer[] = rawOpts.map((o, idx) => {
          let correct = false;
          correctTokens.forEach((tok) => {
            const num = Number(tok);
            if (!Number.isNaN(num) && tok !== '') {
              if (num - 1 === idx) correct = true;
            } else if (
              tok.toLowerCase() === o.name.toLowerCase() ||
              (o.name_ar && tok === o.name_ar)
            ) {
              correct = true;
            }
          });
          if (answerText && answerText.toLowerCase() === o.name.toLowerCase()) correct = true;
          if (answerArText && o.name_ar && answerArText === o.name_ar) correct = true;
          return {
            name: o.name,
            name_ar: o.name_ar || undefined,
            points: correct ? questionPoints : 0,
            minusPoints: 0,
            correct,
          };
        });
        if (!answers.length && answerText) {
          answers = [
            { name: answerText, name_ar: answerArText || undefined, points: questionPoints, minusPoints: 0, correct: true },
          ];
        }
        return buildMCQ({ title, title_ar: qiAr >= 0 ? (c[qiAr] || '').trim() : '', answers });
      })
      .filter((q): q is MCQQuestion => !!q && !!q.title && q.answers.length > 0);
  }

  const wideOpts = header
    .map((h, i) => (/^(option|opt|choice|answer)\d+$/.test(h) ? i : -1))
    .filter((i) => i >= 0);
  const singleAnswer = col(['answer', 'name', 'option', 'choice']);

  // FULL (one row per answer, grouped by question).
  if (singleAnswer >= 0 && wideOpts.length === 0) {
    const qiAr = col(['question_ar', 'title_ar']);
    const desc = col(['description', 'desc']);
    const descAr = col(['description_ar', 'desc_ar']);
    const ansAr = col(['answer_ar', 'name_ar', 'option_ar', 'choice_ar']);
    const pts = col(['points', 'point']);
    const minus = col(['minuspoints', 'minus_points', 'minus']);
    const corr = col(['correct', 'iscorrect', 'is_correct']);
    const groups = new Map<string, any>();
    lines.forEach((line) => {
      const c = splitCsvLine(line);
      const title = (c[qi] || '').trim();
      if (!title) return;
      if (!groups.has(title)) {
        groups.set(title, {
          title,
          title_ar: qiAr >= 0 ? (c[qiAr] || '').trim() : '',
          description: desc >= 0 ? (c[desc] || '').trim() : '',
          description_ar: descAr >= 0 ? (c[descAr] || '').trim() : '',
          answers: [],
        });
      }
      const correct = truthy(c[corr] || '');
      groups.get(title).answers.push({
        name: (c[singleAnswer] || '').trim(),
        name_ar: ansAr >= 0 ? (c[ansAr] || '').trim() : '',
        points: pts >= 0 ? numOr(c[pts], correct ? 1 : 0) : correct ? 1 : 0,
        minusPoints: minus >= 0 ? numOr(c[minus], 0) : 0,
        correct,
      });
    });
    return Array.from(groups.values())
      .map((g) => buildMCQ(g))
      .filter((q) => q.title && q.answers.length);
  }

  // SIMPLE (one row per question): option columns + 1-based correct number(s).
  const correctIdx = col(['correct', 'answer', 'correctindex']);
  const usedOptIdxs = wideOpts.length
    ? wideOpts
    : header.map((_, i) => i).filter((i) => i !== qi && i !== correctIdx);
  return lines
    .map((line) => {
      const c = splitCsvLine(line);
      const title = (c[qi] || '').trim();
      const correctSet = new Set<number>();
      (c[correctIdx] || '').split(/[;|]/).forEach((s) => {
        const n = parseInt(s, 10) - 1; // 1-based in the file
        if (!Number.isNaN(n) && n >= 0) correctSet.add(n);
      });
      const answers: FullAnswer[] = usedOptIdxs
        .map((colIdx, optPos) => ({ nm: (c[colIdx] || '').trim(), optPos }))
        .filter((x) => x.nm !== '')
        .map((x) => ({
          name: x.nm,
          points: correctSet.has(x.optPos) ? 1 : 0,
          minusPoints: 0,
          correct: correctSet.has(x.optPos),
        }));
      return buildMCQ({ title, answers });
    })
    .filter((q) => q.title && q.answers.length);
}
